import React, { useEffect, useState } from 'react';
import AppImage from '../../../common/media/AppImage';
import pageAssets from '../../../../data/pageAssets';
import { openQuoteChat } from '../../../../utils/site';
import { trackEvent } from '../../../../utils/firebase';
import { SITE_URL } from '../../../../utils/seo';
import RouteConstants from '../../../../utils/RouteConstants';
import '../orbitStarVariants.css';

const BULB_COUNT = 13;

// Points along a quadratic curve so the bulbs hang like a real festive string.
const getStrandBulbs = (startY, sagY, endY) => Array.from({ length: BULB_COUNT }, (_, i) => {
    const t = (i + 0.5) / BULB_COUNT;
    const x = 2 * (1 - t) * t * 300 + t * t * 600;
    const y = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * sagY + t * t * endY;
    return { x, y };
});

const STRANDS = [
    { path: 'M0 20 Q300 300 600 20', bulbs: getStrandBulbs(20, 300, 20) },
    { path: 'M0 130 Q300 410 600 130', bulbs: getStrandBulbs(130, 410, 130) }
];

const LightString = () => (
    <svg className="orbit-star-variants-string" viewBox="0 0 600 320" aria-hidden="true">
        {STRANDS.map((strand, strandIndex) => (
            <g key={strand.path}>
                <path d={strand.path} className="orbit-star-variants-wire" />
                {strand.bulbs.map((bulb, i) => (
                    <g key={i} transform={`translate(${bulb.x} ${bulb.y})`}>
                        <rect x="-4" y="0" width="8" height="11" rx="2" className="orbit-star-variants-cap" />
                        <circle
                            cy="21"
                            r="12"
                            className="orbit-star-variants-bulb"
                            style={{ animationDelay: `${((i * 7 + strandIndex * 3) % 10) * 0.25}s` }}
                        />
                    </g>
                ))}
            </g>
        ))}
    </svg>
);

export const toVariantSlug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const OrbitStarVariantPicker = ({
    section,
    onBuy = openQuoteChat,
    className = '',
    syncWithUrl = false,
    initialModelId,
    trackingLocation = 'section'
}) => {
    const [modelIndex, setModelIndex] = useState(() => Math.max(section.models.findIndex((item) => item.id === initialModelId), 0));
    const [colourIndex, setColourIndex] = useState(0);
    const [imageIndex, setImageIndex] = useState(0);

    // Applied after mount so the react-snap HTML (default variant) still hydrates cleanly.
    useEffect(() => {
        if (!syncWithUrl) return;
        const params = new URLSearchParams(window.location.search);
        const sizeIndex = section.models.findIndex((item) => item.id === params.get('size'));
        const nextModel = section.models[Math.max(sizeIndex, 0)];
        const nextColour = nextModel.items.findIndex((item) => toVariantSlug(item.colour) === params.get('colour'));
        if (sizeIndex > 0) setModelIndex(sizeIndex);
        if (nextColour > 0) setColourIndex(nextColour);
    }, [syncWithUrl, section]);

    const model = section.models[modelIndex];
    const active = model.items[colourIndex];
    const images = [
        ...(active.imageKey ? [{ imageKey: active.imageKey, imageAlt: active.imageAlt }] : []),
        ...model.gallery
    ];
    const image = images[imageIndex];
    const discount = model.price && model.mrp ? Math.round((1 - model.price / model.mrp) * 100) : null;
    const priceText = model.price ? `${section.currencySymbol}${model.price}` : section.priceOnRequestLabel;
    const variantUrl = `${SITE_URL}${RouteConstants.ORBIT_STAR}?size=${model.id}&colour=${toVariantSlug(active.colour)}#variants`;
    const shareText = section.shareTemplate
        .replace('{length}', model.label)
        .replace('{colour}', active.colour)
        .replace('{price}', priceText)
        .replace('{url}', variantUrl);

    const variantParams = (nextModel, item) => ({
        item_id: item.sku || `${nextModel.model}-${toVariantSlug(item.colour)}`,
        item_name: `Orbit Star ${nextModel.label}`,
        item_variant: item.colour,
        price: nextModel.price,
        location: trackingLocation
    });

    const selectModel = (index) => {
        const nextModel = section.models[index];
        const nextColour = Math.min(colourIndex, nextModel.items.length - 1);
        setModelIndex(index);
        setColourIndex(nextColour);
        setImageIndex(0);
        trackEvent('select_item', variantParams(nextModel, nextModel.items[nextColour]));
    };

    const selectColour = (index) => {
        setColourIndex(index);
        setImageIndex(0);
        trackEvent('select_item', variantParams(model, model.items[index]));
    };

    return (
        <div className={`orbit-star-variants-picker-root ${className}`} style={{ '--variant': active.swatch }}>
            <div className="orbit-star-variants-ambient" aria-hidden="true" />
            <div className="orbit-star-variants-showcase">
                <div className="orbit-star-variants-gallery">
                    <div className={`orbit-star-variants-stage${image ? ' has-photo' : ''}`}>
                        <span className="orbit-star-variants-stage-label">
                            {active.colour} · {model.label}
                        </span>
                        {image ? (
                            <AppImage
                                key={image.imageKey}
                                className="orbit-star-variants-photo"
                                src={pageAssets[image.imageKey]}
                                alt={image.imageAlt}
                                width="600"
                                height="600"
                            />
                        ) : (
                            <LightString />
                        )}
                    </div>

                    {images.length > 1 ? (
                        <div className="orbit-star-variants-thumbs">
                            {images.map((item, index) => (
                                <button
                                    key={item.imageKey}
                                    type="button"
                                    className={`orbit-star-variants-thumb${index === imageIndex ? ' is-active' : ''}`}
                                    aria-pressed={index === imageIndex}
                                    aria-label={item.imageAlt}
                                    onClick={() => setImageIndex(index)}
                                >
                                    <AppImage src={pageAssets[`${item.imageKey}Thumb`] || pageAssets[item.imageKey]} alt="" width="120" height="120" />
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="orbit-star-variants-panel">
                    <span className="orbit-star-variants-model">
                        {section.modelLabel} {model.model}
                    </span>
                    <h3 className="orbit-star-variants-name">{active.colour}</h3>
                    <p className="orbit-star-variants-copy">{active.description}</p>

                    <div className="orbit-star-variants-price">
                        {model.price ? (
                            <>
                                <span className="orbit-star-variants-price-current">
                                    {section.currencySymbol}{model.price}
                                </span>
                                {model.mrp ? (
                                    <>
                                        <span className="orbit-star-variants-price-mrp">
                                            {section.mrpLabel} <s>{section.currencySymbol}{model.mrp}</s>
                                        </span>
                                        <span className="orbit-star-variants-price-off">{discount}% {section.offLabel}</span>
                                    </>
                                ) : null}
                            </>
                        ) : (
                            <span className="orbit-star-variants-price-request">{section.priceOnRequestLabel}</span>
                        )}
                    </div>

                    <div className="orbit-star-variants-picker-label">{section.sizeLabel}</div>
                    <div className="orbit-star-variants-sizes" role="group" aria-label={section.sizeLabel}>
                        {section.models.map((item, index) => (
                            <button
                                key={item.id}
                                type="button"
                                className={`orbit-star-variants-size${index === modelIndex ? ' is-active' : ''}`}
                                aria-pressed={index === modelIndex}
                                onClick={() => selectModel(index)}
                            >
                                <strong>{item.label}</strong>
                                <span>
                                    {item.sublabel}
                                    {item.price ? ` · ${section.currencySymbol}${item.price}` : null}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="orbit-star-variants-picker-label">
                        {section.pickerLabel}
                        {active.sku ? <span>{section.skuLabel}: {active.sku}</span> : null}
                    </div>
                    <div className="orbit-star-variants-picker" role="group" aria-label={section.pickerLabel}>
                        {model.items.map((item, index) => (
                            <button
                                key={item.colour}
                                type="button"
                                className={`orbit-star-variants-swatch${index === colourIndex ? ' is-active' : ''}`}
                                style={{ '--swatch': item.swatch }}
                                aria-pressed={index === colourIndex}
                                onClick={() => selectColour(index)}
                            >
                                <span className="orbit-star-variants-swatch-dot" aria-hidden="true" />
                                {item.colour}
                            </button>
                        ))}
                    </div>

                    <ul className="orbit-star-variants-chips">
                        {model.summary.map((item) => <li key={item}>{item}</li>)}
                    </ul>

                    <div className="orbit-star-variants-actions">
                        {active.buyUrl ? (
                            <a
                                className="orbit-star-variants-buy"
                                href={active.buyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackEvent('buy_on_amazon', variantParams(model, active))}
                            >
                                {section.amazonButtonLabel}
                            </a>
                        ) : (
                            <button type="button" className="orbit-star-variants-buy" onClick={onBuy}>
                                {section.buttonLabel}
                            </button>
                        )}
                        <button type="button" className="orbit-star-variants-enquire" onClick={onBuy}>
                            {section.secondaryButtonLabel}
                        </button>
                    </div>

                    <div className="orbit-star-variants-extras">
                        {active.buyUrl ? <span className="orbit-star-variants-note">{section.amazonNote}</span> : null}
                        <a
                            className="orbit-star-variants-share"
                            href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackEvent('share', { method: 'whatsapp', content_type: 'product', ...variantParams(model, active) })}
                        >
                            {section.shareLabel}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrbitStarVariantPicker;
